import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { SUPPLY_STATUSES } from '../../utils/statuses';
import { fmt, fmtDt, money, errMsg } from '../../utils/helpers';
import { Badge, Spinner, EmptyState, Tabs, Select, Modal, ConfirmModal, Field } from '../../components/common/UI';
import { CommentsPanel, FilesPanel, ActivityLog } from '../../components/common/Panels';
import { PaymentModal } from '../../components/common/PaymentModal';
import { NewSupplyModal } from '../NewEntityModals';
import { ArrowLeft, Plus, RefreshCw, Save, Edit2, X, Calendar, Truck, Trash2, Package } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_OPTS = Object.entries(SUPPLY_STATUSES).map(([v, d]) => ({ value: v, label: d.label }));

// ── СПИСОК ────────────────────────────────────────────────────
export function SuppliesPage() {
  const { hasPerm, isProducer } = useAuth();
  const navigate = useNavigate();
  const [supplies, setSupplies] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [page, setPage] = useState(1);
  const LIMIT = 50;


  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/supplies', { params: { status, page, limit: LIMIT } });
      setSupplies(data.data);
      setTotal(data.total);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  }, [status, page]);

  useEffect(() => { setPage(1); }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-100">Поставки</h1>
          <p className="text-xs text-gray-600 mt-0.5">{total} записей</p>
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost p-2" onClick={load}><RefreshCw size={15} className={loading ? 'animate-spin' : ''} /></button>
          {hasPerm('supplies', 'create') && (
            <button className="btn-primary" onClick={() => setShowNew(true)}><Plus size={15} /> Новая поставка</button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => setStatus('')}
          className={'badge border cursor-pointer transition-all ' + (!status ? 'bg-gray-700 text-gray-200 border-gray-600' : 'bg-transparent text-gray-500 border-gray-700 hover:border-gray-500')}>
          Все активные
        </button>
        {Object.entries(SUPPLY_STATUSES).map(([k, v]) => (
          <button key={k} onClick={() => setStatus(status === k ? '' : k)}
            className={'badge border cursor-pointer transition-all ' + (status === k ? v.color : 'bg-transparent text-gray-500 border-gray-700 hover:border-gray-500')}>
            {v.label}
          </button>
        ))}
      </div>

      <div className="card overflow-hidden">
        {loading && !supplies.length
          ? <div className="flex justify-center py-12"><Spinner size={24} /></div>
          : !supplies.length ? <EmptyState text="Нет поставок" />
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium w-28">Номер</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Описание</th>
                    {isProducer && <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Дилер</th>}
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium w-40">Статус</th>
                    <th className="text-right px-4 py-3 text-xs text-gray-500 font-medium w-28">Сумма</th>
                    <th className="text-center px-4 py-3 text-xs text-gray-500 font-medium w-20">Позиций</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium w-28">ETA склад</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium w-28">Создана</th>
                  </tr>
                </thead>
                <tbody>
                  {supplies.map(s => {
                    const st = SUPPLY_STATUSES[s.status] || {};
                    return (
                      <tr key={s.id} className="table-row" onClick={() => navigate('/supplies/' + s.id)}>
                        <td className="px-4 py-3"><span className="mono text-blue-400 text-xs">{s.supply_number}</span></td>
                        <td className="px-4 py-3 text-gray-200 font-medium max-w-xs truncate">{s.description}</td>
                        {isProducer && <td className="px-4 py-3 text-gray-400">{s.dealer_name}</td>}
                        <td className="px-4 py-3"><Badge className={st.color}>{st.label}</Badge></td>
                        <td className="px-4 py-3 text-right mono text-gray-300">{money(s.amount)}</td>
                        <td className="px-4 py-3 text-center text-gray-500">{s.items_count || 0}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{fmt(s.eta_to_warehouse) || '—'}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">{fmt(s.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>
      {total > LIMIT && (
        <div className="flex items-center justify-center gap-2">
          <button className="btn-secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>←</button>
          <span className="text-sm text-gray-500">стр. {page} из {Math.ceil(total / LIMIT)}</span>
          <button className="btn-secondary" disabled={page * LIMIT >= total} onClick={() => setPage(p => p + 1)}>→</button>
        </div>
      )}

      {showNew && <NewSupplyModal onClose={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

// ── КАРТОЧКА ПОСТАВКИ ─────────────────────────────────────────
export function SupplyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isProducer, hasPerm } = useAuth();

  const [supply, setSupply] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('info');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ description: '', supplier_info: '', payer_company_id: '', receiver_company_id: '', eta_to_warehouse: '' });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [companies, setCompanies] = useState([]);

  const [statusModal, setStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [paymentModal, setPaymentModal] = useState(false);

  // Позиции
  const [addItemModal, setAddItemModal] = useState(false);
  const [editItemModal, setEditItemModal] = useState(null);
  const [deleteItemConfirm, setDeleteItemConfirm] = useState(null);
  const [itemForm, setItemForm] = useState({ name: '', quantity: 1, unit_price: '', notes: '' });

  // Отгрузки
  const [shipmentModal, setShipmentModal] = useState(false);
  const [deleteShipmentConfirm, setDeleteShipmentConfirm] = useState(null);
  const [deletePaymentConfirm, setDeletePaymentConfirm] = useState(null);
  const [shipmentForm, setShipmentForm] = useState({ shipped_at: new Date().toISOString().slice(0, 10), note: '', items: {} });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/supplies/' + id);
      setSupply(data);
      setForm({
        description: data.description,
        supplier_info: data.supplier_info || '',
        payer_company_id: data.payer_company_id || '',
        receiver_company_id: data.receiver_company_id || '',
        eta_to_warehouse: data.eta_to_warehouse ? data.eta_to_warehouse.slice(0, 10) : '',
      });
      setNewStatus(data.status);
    } catch { navigate('/supplies'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!editing && !paymentModal) return;
    api.get('/admin/companies').then(({ data }) => setCompanies(data));
  }, [editing, paymentModal]);

  const targetDealerId = supply?.dealer_id;
  const myCompanies = companies.filter(c => !c.dealer_id).map(c => ({ value: c.id, label: c.name }));
  const dealerCompanies = companies.filter(c => targetDealerId ? c.dealer_id === targetDealerId : c.dealer_id)
    .map(c => ({ value: c.id, label: c.name }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/supplies/' + id, form);
      toast.success('Сохранено');
      setEditing(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const deleteSupply = async () => {
    try {
      await api.delete('/supplies/' + id);
      toast.success('Поставка удалена');
      navigate('/supplies');
    } catch (err) { toast.error(errMsg(err)); }
  };

  const changeStatus = async () => {
    try {
      await api.patch('/supplies/' + id + '/status', { status: newStatus, note: statusNote });
      toast.success('Статус изменён');
      setStatusModal(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  // ── Позиции ────────────────────────────────────────────────
  const openAddItem = () => {
    setItemForm({ name: '', quantity: 1, unit_price: '', notes: '' });
    setAddItemModal(true);
  };

  const openEditItem = (item) => {
    setItemForm({ name: item.name, quantity: item.quantity, unit_price: item.unit_price, notes: item.notes || '' });
    setEditItemModal(item);
  };

  const saveItem = async () => {
    if (!itemForm.name) { toast.error('Укажите название'); return; }
    setSaving(true);
    try {
      if (addItemModal) {
        await api.post('/supplies/' + id + '/items', itemForm);
        toast.success('Позиция добавлена');
        setAddItemModal(false);
      } else {
        await api.patch('/supplies/' + id + '/items/' + editItemModal.id, itemForm);
        toast.success('Сохранено');
        setEditItemModal(null);
      }
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const deleteItem = async (item) => {
    try {
      await api.delete('/supplies/' + id + '/items/' + item.id);
      toast.success('Позиция удалена');
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  // ── Отгрузки ───────────────────────────────────────────────
  const shippedMap = {};
  supply?.shipments?.forEach(s => s.items?.forEach(si => {
    shippedMap[si.supply_item_id] = (shippedMap[si.supply_item_id] || 0) + +si.quantity;
  }));

  const availableItems = (supply?.items || []).filter(i => (+i.quantity - (shippedMap[i.id] || 0)) > 0);
  const allShipped = supply?.items?.length > 0 && supply.items.every(i => (shippedMap[i.id] || 0) >= +i.quantity);

  const openShipmentModal = () => {
    const init = {};
    availableItems.forEach(i => { init[i.id] = +i.quantity - (shippedMap[i.id] || 0); });
    setShipmentForm({ shipped_at: new Date().toISOString().slice(0, 10), note: '', items: init });
    setShipmentModal(true);
  };

  const createShipment = async () => {
    const items = Object.entries(shipmentForm.items)
      .filter(([, q]) => +q > 0)
      .map(([id, q]) => ({ supply_item_id: +id, quantity: +q }));
    if (!items.length) { toast.error('Укажите позиции для отгрузки'); return; }
    setSaving(true);
    try {
      await api.post('/supplies/' + id + '/shipments', {
        shipped_at: shipmentForm.shipped_at,
        note: shipmentForm.note,
        items,
      });
      toast.success('Отгрузка создана');
      setShipmentModal(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const deleteShipment = async (shipmentId) => {
    try {
      await api.delete('/supplies/' + id + '/shipments/' + shipmentId);
      toast.success('Отгрузка удалена');
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const deletePayment = async (paymentId) => {
    try {
      await api.delete('/supplies/' + id + '/payments/' + paymentId);
      toast.success('Платёж удалён');
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  if (loading) return <div className="flex justify-center items-center h-64"><Spinner size={32} /></div>;

  const st = supply ? SUPPLY_STATUSES[supply.status] : null;
  const totalPaid = supply?.payments?.reduce((s, p) => s + +p.amount, 0) || 0;
  const totalIn = supply?.payments?.filter(p => p.direction === 'in').reduce((s, p) => s + +p.amount, 0) || 0;
  const totalOut = supply?.payments?.filter(p => p.direction === 'out').reduce((s, p) => s + +p.amount, 0) || 0;
  const canChangeSupplyStatus = isProducer && hasPerm('supplies', 'edit_status');
  const canEditSupply = hasPerm('supplies', 'edit_content');
  const canManageSupplyPayments = isProducer && hasPerm('supplies', 'add_payment');
  const canDeleteSupply = user?.role === 'superadmin';

  const TABS = [
    { id: 'info',      label: 'Информация' },
    { id: 'items',     label: 'Позиции',         count: supply?.items?.length },
    { id: 'shipments', label: 'Отгрузки дилеру', count: supply?.shipments?.length },
    ...(canManageSupplyPayments ? [{ id: 'payments',  label: 'Движение средств', count: supply?.payments?.length }] : []),
    { id: 'comments',  label: 'Комментарии',      count: supply?.comments?.length },
    { id: 'files',     label: 'Файлы',            count: supply?.files?.length },
    { id: 'history',   label: 'История' },
  ];

  return (
    <div className="p-4 md:p-6 max-w-5xl">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <button className="btn-ghost p-2" onClick={() => navigate('/supplies')}><ArrowLeft size={16} /></button>
          <div>
            <div className="flex items-center gap-2">
              <span className="mono text-blue-400 text-sm">{supply?.supply_number}</span>
              {st && <Badge className={st.color}>{st.label}</Badge>}
              {allShipped && supply?.items?.length > 0 && <Badge className="bg-green-900/50 text-green-300 border-green-700">✓ Отгружено</Badge>}
            </div>
            <h1 className="text-lg font-bold text-gray-100 mt-0.5 max-w-lg line-clamp-1">{supply?.description}</h1>
            {supply?.eta_to_warehouse && (
              <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                <Calendar size={11} /> ETA: {fmt(supply.eta_to_warehouse)}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {canChangeSupplyStatus && <button className="btn-secondary" onClick={() => { setNewStatus(supply.status); setStatusModal(true); }}>Статус</button>}
          {canEditSupply && !editing && <button className="btn-secondary" onClick={() => setEditing(true)}><Edit2 size={14} /></button>}
          {editing && <button className="btn-ghost" onClick={() => setEditing(false)}><X size={14} /></button>}
          {editing && <button className="btn-primary" onClick={save} disabled={saving}>{saving ? <Spinner size={14} /> : <><Save size={14} /> Сохранить</>}</button>}
          {canDeleteSupply && !editing && <button className="btn-danger" onClick={() => setDeleteConfirm(true)}><Trash2 size={14} /> Удалить</button>}
        </div>
      </div>

      <div className="card overflow-hidden">
        <Tabs tabs={TABS} active={tab} onChange={setTab} />
        <div className="p-5">

          {/* ── ИНФОРМАЦИЯ ── */}
          {tab === 'info' && (
            editing ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Field label="Описание поставки" required>
                    <textarea className="input resize-none" rows={3} value={form.description}
                      onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <Field label="Поставщик" hint="Название, контакты, реквизиты">
                    <textarea className="input resize-none" rows={3} value={form.supplier_info}
                      onChange={e => setForm(f => ({ ...f, supplier_info: e.target.value }))}
                      placeholder="Название компании, контактное лицо, телефон..." />
                  </Field>
                </div>
                <Field label="Плательщик (дилер)">
                  <Select value={form.payer_company_id} onChange={v => setForm(f => ({ ...f, payer_company_id: v }))}
                    options={dealerCompanies} placeholder="Компания" />
                </Field>
                <Field label="Получатель (моя компания)">
                  <Select value={form.receiver_company_id} onChange={v => setForm(f => ({ ...f, receiver_company_id: v }))}
                    options={myCompanies} placeholder="Компания" />
                </Field>
                <Field label="Прогноз поступления на склад">
                  <input type="date" className="input" value={form.eta_to_warehouse}
                    onChange={e => setForm(f => ({ ...f, eta_to_warehouse: e.target.value }))} />
                </Field>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-4">
                  <div>
                    <div className="section-title mb-2">Описание</div>
                    <p className="text-sm text-gray-300 whitespace-pre-wrap">{supply?.description}</p>
                  </div>
                  {supply?.supplier_info && (
                    <div>
                      <div className="section-title mb-2">Поставщик</div>
                      <p className="text-sm text-gray-300 whitespace-pre-wrap">{supply.supplier_info}</p>
                    </div>
                  )}
                  <div>
                    <div className="section-title mb-3">История статусов</div>
                    {supply?.statusLog?.map((s, i) => {
                      const stInfo = SUPPLY_STATUSES[s.status] || {};
                      return (
                        <div key={s.id} className="flex gap-3">
                          <div className="flex flex-col items-center">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                            {i < supply.statusLog.length - 1 && <div className="w-px flex-1 bg-gray-800 my-1" />}
                          </div>
                          <div className="pb-3">
                            <Badge className={stInfo.color}>{stInfo.label}</Badge>
                            <div className="text-xs text-gray-500 mt-0.5">{fmtDt(s.changed_at)} · {s.by_name}</div>
                            {s.note && <div className="text-xs text-gray-400 italic mt-0.5">{s.note}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-3 text-sm">
                  {isProducer && <div><div className="section-title mb-1">Дилер</div><div className="text-gray-300">{supply?.dealer_name}</div></div>}
                  <div><div className="section-title mb-1">ETA склад</div><div className="text-gray-300">{fmt(supply?.eta_to_warehouse) || '—'}</div></div>
                  <div><div className="section-title mb-1">Итого сумма</div><div className="mono text-gray-200">{money(supply?.amount)}</div></div>
                  <div><div className="section-title mb-1">Плательщик</div><div className="text-gray-300">{supply?.payer_company_name || '—'}</div></div>
                  <div><div className="section-title mb-1">Получатель</div><div className="text-gray-300">{supply?.receiver_company_name || '—'}</div></div>
                </div>
              </div>
            )
          )}

          {/* ── ПОЗИЦИИ ── */}
          {tab === 'items' && (
            <div className="space-y-4">
              {isProducer && canEditSupply && (
                <button className="btn-primary" onClick={openAddItem}><Plus size={14} /> Добавить позицию</button>
              )}

              {!supply?.items?.length ? (
                <p className="text-sm text-gray-600">Нет позиций</p>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2 text-xs text-gray-500 uppercase tracking-wide px-1">
                    <div className="col-span-5">Наименование</div>
                    <div className="col-span-2 text-center">Кол-во</div>
                    <div className="col-span-2 text-right">Цена ед.</div>
                    <div className="col-span-2 text-right">Сумма</div>
                    <div className="col-span-1"></div>
                  </div>
                  {supply.items.map(item => {
                    const shipped = shippedMap[item.id] || 0;
                    const remaining = +item.quantity - shipped;
                    return (
                      <div key={item.id} className="grid grid-cols-12 gap-2 items-center px-3 py-2.5 bg-gray-800/60 border border-gray-800 rounded-lg">
                        <div className="col-span-5">
                          <div className="text-sm text-gray-200">{item.name}</div>
                          {item.notes && <div className="text-xs text-gray-500 italic">{item.notes}</div>}
                          {shipped > 0 && (
                            <div className="text-xs text-gray-500">Отгружено: {shipped} / {item.quantity}</div>
                          )}
                        </div>
                        <div className="col-span-2 text-center text-sm text-gray-300">{item.quantity}</div>
                        <div className="col-span-2 text-right mono text-sm text-gray-300">{money(item.unit_price)}</div>
                        <div className="col-span-2 text-right mono text-sm font-medium text-gray-200">{money(+item.quantity * +item.unit_price)}</div>
                        <div className="col-span-1 flex justify-end gap-1">
                          {canEditSupply && (
                            <>
                              <button className="btn-ghost p-1" onClick={() => openEditItem(item)}><Edit2 size={13} /></button>
                              <button className="btn-ghost p-1 hover:text-red-400" onClick={() => setDeleteItemConfirm(item)}><Trash2 size={13} /></button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div className="flex justify-between items-center pt-2 border-t border-gray-700 text-sm">
                    <span className="text-gray-500">Итого:</span>
                    <span className="mono font-medium text-gray-200">{money(supply.amount)}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── ОТГРУЗКИ ДИЛЕРУ ── */}
          {tab === 'shipments' && (
            <div className="space-y-4">
              {canChangeSupplyStatus && (
                <div className="flex items-center gap-3">
                  <button className="btn-primary" onClick={openShipmentModal} disabled={!availableItems.length}>
                    <Truck size={14} /> Создать отгрузку дилеру
                  </button>
                  {allShipped && supply?.items?.length > 0 && (
                    <span className="text-xs text-green-400">✓ Все позиции отгружены</span>
                  )}
                </div>
              )}

              {!supply?.shipments?.length ? (
                <p className="text-sm text-gray-600">Отгрузок нет</p>
              ) : (
                <div className="space-y-3">
                  {supply.shipments.map(s => (
                    <div key={s.id} className="card p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <Truck size={15} className="text-purple-400" />
                            <span className="font-medium text-gray-200">Отгрузка {fmt(s.shipped_at)}</span>
                          </div>
                          {s.note && <div className="text-xs text-gray-500 ml-5">{s.note}</div>}
                          <div className="text-xs text-gray-600 ml-5">{fmtDt(s.created_at)} · {s.created_by_name}</div>
                        </div>
                        {canChangeSupplyStatus && (
                          <button className="btn-ghost p-1.5 hover:text-red-400" onClick={() => setDeleteShipmentConfirm(s)}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                      <div className="space-y-1 pl-5">
                        {s.items?.map(si => (
                          <div key={si.id} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2 text-gray-300">
                              <Package size={13} className="text-gray-600" />
                              {si.item_name}
                            </div>
                            <span className="text-gray-400 mono">× {si.quantity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── ДВИЖЕНИЕ СРЕДСТВ ── */}
          {tab === 'payments' && (
            <div className="space-y-4">
              {canManageSupplyPayments && (
                <button className="btn-primary" onClick={() => setPaymentModal(true)}><Plus size={14} /> Добавить движение средств</button>
              )}
              {!supply?.payments?.length && <p className="text-sm text-gray-600">Нет платежей</p>}
              {supply?.payments?.map(p => (
                <div key={p.id} className="p-3 bg-gray-800/60 border border-gray-800 rounded-lg group">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-0.5 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={'text-xs px-1.5 py-0.5 rounded ' + (p.direction === 'in' ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400')}>
                          {p.direction === 'in' ? '↓ Входящий' : '↑ Исходящий'}
                        </span>
                        <span className="font-medium text-gray-200">{money(p.amount)}</span>
                      </div>
                      {(p.payer_name || p.receiver_name) && <div className="text-xs text-gray-500">{p.payer_name} → {p.receiver_name}</div>}
                      {p.doc_number && <div className="text-xs text-gray-500 mono">№ {p.doc_number}{p.doc_date ? ' от ' + fmt(p.doc_date) : ''}</div>}
                      {p.payment_date && <div className="text-xs text-gray-500">Дата платежа: {fmt(p.payment_date)}</div>}
                      {p.note && <div className="text-xs text-gray-600 italic">{p.note}</div>}
                      <div className="text-xs text-gray-700">{p.by_name}</div>
                    </div>
                    {canManageSupplyPayments && (
                      <button className="btn-ghost p-1 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                        onClick={() => setDeletePaymentConfirm(p)}><Trash2 size={14} /></button>
                    )}
                  </div>
                </div>
              ))}
              {supply?.payments?.length > 0 && (
                <div className="border-t border-gray-700 pt-3 space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">↓ Получено от дилера:</span><span className="mono text-green-400">{money(totalIn)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">↑ Перечислено поставщику:</span><span className="mono text-red-400">{money(totalOut)}</span></div>
                  <div className="flex justify-between font-medium border-t border-gray-800 pt-1"><span className="text-gray-400">Баланс:</span><span className={'mono ' + (totalIn - totalOut >= 0 ? 'text-gray-200' : 'text-yellow-400')}>{money(totalIn - totalOut)}</span></div>
                </div>
              )}
            </div>
          )}

          {tab === 'comments' && <CommentsPanel entityType="supply" entityId={id} comments={supply?.comments} onRefresh={load} />}
          {tab === 'files' && <FilesPanel entityType="supply" entityId={id} files={supply?.files} onRefresh={load} />}
          {tab === 'history' && <ActivityLog entries={supply?.actLog} />}
        </div>
      </div>

      {/* ── МОДАЛКИ ── */}
      {deleteConfirm && (
        <ConfirmModal message={'Удалить поставку ' + supply?.supply_number + '? Это действие нельзя отменить.'}
          onConfirm={deleteSupply}
          onClose={() => setDeleteConfirm(false)} />
      )}

      {statusModal && (
        <Modal title="Сменить статус" onClose={() => setStatusModal(false)} size="sm">
          <div className="space-y-4">
            <Field label="Новый статус"><Select value={newStatus} onChange={setNewStatus} options={STATUS_OPTS} /></Field>
            <Field label="Примечание"><input className="input" value={statusNote} onChange={e => setStatusNote(e.target.value)} /></Field>
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setStatusModal(false)}>Отмена</button>
              <button className="btn-primary" onClick={changeStatus}>Применить</button>
            </div>
          </div>
        </Modal>
      )}

      {paymentModal && (
        <PaymentModal entityType="supply" entityId={id}
          onClose={() => setPaymentModal(false)} onDone={load} />
      )}

      {(addItemModal || editItemModal) && (
        <Modal title={addItemModal ? 'Добавить позицию' : 'Редактировать позицию'}
          onClose={() => { setAddItemModal(false); setEditItemModal(null); }} size="sm">
          <div className="space-y-3">
            <Field label="Наименование" required>
              <input className="input" value={itemForm.name} autoFocus
                onChange={e => setItemForm(f => ({ ...f, name: e.target.value }))} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Количество">
                <input type="number" min="1" step="1" className="input" value={itemForm.quantity}
                  onChange={e => setItemForm(f => ({ ...f, quantity: e.target.value }))} />
              </Field>
              <Field label="Цена за единицу, ₽">
                <input type="number" min="0" step="0.01" className="input" value={itemForm.unit_price}
                  onChange={e => setItemForm(f => ({ ...f, unit_price: e.target.value }))} />
              </Field>
            </div>
            {itemForm.quantity && itemForm.unit_price && (
              <div className="text-xs text-gray-500 text-right">
                Сумма: <span className="mono text-gray-300">{money(+itemForm.quantity * +itemForm.unit_price)}</span>
              </div>
            )}
            <Field label="Примечание">
              <input className="input" value={itemForm.notes}
                onChange={e => setItemForm(f => ({ ...f, notes: e.target.value }))} />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-secondary" onClick={() => { setAddItemModal(false); setEditItemModal(null); }}>Отмена</button>
              <button className="btn-primary" onClick={saveItem} disabled={saving}>
                {saving ? <Spinner size={14} /> : (addItemModal ? 'Добавить' : 'Сохранить')}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {deleteItemConfirm && (
        <ConfirmModal message={'Удалить позицию "' + deleteItemConfirm.name + '"?'}
          onConfirm={() => deleteItem(deleteItemConfirm)}
          onClose={() => setDeleteItemConfirm(null)} />
      )}

      {shipmentModal && (
        <Modal title="Создать отгрузку дилеру" onClose={() => setShipmentModal(false)} size="md">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Дата отгрузки">
                <input type="date" className="input" value={shipmentForm.shipped_at}
                  onChange={e => setShipmentForm(f => ({ ...f, shipped_at: e.target.value }))} />
              </Field>
              <Field label="Примечание">
                <input className="input" value={shipmentForm.note}
                  onChange={e => setShipmentForm(f => ({ ...f, note: e.target.value }))} />
              </Field>
            </div>
            <div>
              <div className="label mb-2">Позиции к отгрузке</div>
              <div className="space-y-2">
                {availableItems.map(item => {
                  const remaining = +item.quantity - (shippedMap[item.id] || 0);
                  const val = shipmentForm.items[item.id] ?? remaining;
                  return (
                    <div key={item.id} className="flex items-center gap-3 p-2.5 bg-gray-800/60 rounded-lg border border-gray-800">
                      <div className="flex-1">
                        <div className="text-sm text-gray-200">{item.name}</div>
                        <div className="text-xs text-gray-500">Остаток: {remaining} из {item.quantity}</div>
                      </div>
                      <input type="number" min="0" max={remaining} step="1"
                        className="input w-24 text-right py-1" value={val}
                        onChange={e => setShipmentForm(f => ({ ...f, items: { ...f.items, [item.id]: +e.target.value } }))} />
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-secondary" onClick={() => setShipmentModal(false)}>Отмена</button>
              <button className="btn-primary" onClick={createShipment} disabled={saving}>
                {saving ? <Spinner size={14} /> : <><Truck size={14} /> Сохранить отгрузку</>}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {deletePaymentConfirm && (
        <ConfirmModal message={'Удалить платёж на ' + money(deletePaymentConfirm.amount) + '?'}
          onConfirm={() => { deletePayment(deletePaymentConfirm.id); setDeletePaymentConfirm(null); }}
          onClose={() => setDeletePaymentConfirm(null)} />
      )}

      {deleteShipmentConfirm && (
        <ConfirmModal message={'Удалить отгрузку от ' + fmt(deleteShipmentConfirm.shipped_at) + '?'}
          onConfirm={() => deleteShipment(deleteShipmentConfirm.id)}
          onClose={() => setDeleteShipmentConfirm(null)} />
      )}
    </div>
  );
}
